'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Camera, Save } from 'lucide-react';
import { toast } from 'sonner';

interface ProfileFormData {
  fullName: string;
  email: string;
  jobTitle: string;
  department: string;
  timezone: string;
  role: string;
  phone: string;
  bio: string;
}

const timezones = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)' },
  { value: 'Europe/Berlin', label: 'Central European Time (CET)' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' },
  { value: 'Asia/Singapore', label: 'Singapore Time (SGT)' },
];

export default function ProfileSettings() {
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<ProfileFormData>({
    defaultValues: {
      fullName: 'Alex Mercer',
      email: 'alex.mercer@nexusmetrics.io',
      jobTitle: 'Chief Operating Officer',
      department: 'Operations',
      timezone: 'America/New_York',
      role: 'executive',
      phone: '+1 (415) 892-3401',
      bio: 'COO at NexusMetrics with 12 years of operations experience across SaaS and fintech.',
    },
  });

  const onSubmit = () => {
    setIsSaving(true);
    // Backend integration point: PATCH /api/user/profile
    setTimeout(() => {
      setIsSaving(false);
      setSaved(true);
      toast.success('Profile updated successfully');
      setTimeout(() => setSaved(false), 3000);
    }, 1200);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Avatar section */}
      <div className="card-surface p-6">
        <h2 className="text-base font-semibold text-foreground mb-4">Profile Photo</h2>
        <div className="flex items-center gap-5">
          <div className="relative">
            <div className="w-20 h-20 rounded-2xl bg-primary/20 flex items-center justify-center">
              <span className="text-2xl font-bold text-primary">AM</span>
            </div>
            <button
              type="button"
              className="absolute -bottom-1 -right-1 w-7 h-7 bg-primary text-white rounded-lg flex items-center justify-center hover:bg-primary/90 transition-colors shadow-card"
              aria-label="Change profile photo"
            >
              <Camera size={13} />
            </button>
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">Alex Mercer</p>
            <p className="text-xs text-muted-foreground mt-0.5">Chief Operating Officer</p>
            <button
              type="button"
              className="mt-2 text-xs text-primary hover:text-primary/80 font-medium transition-colors"
            >
              Upload new photo
            </button>
          </div>
        </div>
      </div>

      {/* Personal info */}
      <div className="card-surface p-6">
        <h2 className="text-base font-semibold text-foreground mb-4">Personal Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="ps-fullname">
              Full Name
            </label>
            <input
              id="ps-fullname"
              type="text"
              className={`w-full bg-input border rounded-lg px-3.5 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all ${errors.fullName ? 'border-negative' : 'border-border'}`}
              {...register('fullName', { required: 'Full name is required' })}
            />
            {errors.fullName && <p className="mt-1 text-xs text-negative">{errors.fullName.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="ps-email">
              Work Email
            </label>
            <input
              id="ps-email"
              type="email"
              className="w-full bg-muted border border-border rounded-lg px-3.5 py-2.5 text-sm text-muted-foreground cursor-not-allowed"
              disabled
              {...register('email')}
            />
            <p className="mt-1 text-xs text-muted-foreground">Contact support to change your email</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="ps-title">
              Job Title
            </label>
            <input
              id="ps-title"
              type="text"
              className="w-full bg-input border border-border rounded-lg px-3.5 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
              {...register('jobTitle')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="ps-dept">
              Department
            </label>
            <input
              id="ps-dept"
              type="text"
              className="w-full bg-input border border-border rounded-lg px-3.5 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
              {...register('department')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="ps-phone">
              Phone Number
            </label>
            <input
              id="ps-phone"
              type="tel"
              className="w-full bg-input border border-border rounded-lg px-3.5 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
              {...register('phone')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="ps-role">
              Dashboard Role
            </label>
            <p className="text-xs text-muted-foreground mb-1.5">Controls your default view and data access</p>
            <select
              id="ps-role"
              className="w-full bg-input border border-border rounded-lg px-3.5 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
              {...register('role')}
            >
              <option value="executive">Executive</option>
              <option value="sales">Sales Manager</option>
              <option value="operations">Operations Manager</option>
              <option value="analyst">Business Analyst</option>
            </select>
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="ps-timezone">
            Timezone
          </label>
          <p className="text-xs text-muted-foreground mb-1.5">Used for report scheduling and data timestamps</p>
          <select
            id="ps-timezone"
            className="w-full bg-input border border-border rounded-lg px-3.5 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
            {...register('timezone')}
          >
            {timezones.map((tz) => (
              <option key={`tz-${tz.value}`} value={tz.value}>{tz.label}</option>
            ))}
          </select>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="ps-bio">
            Short Bio
          </label>
          <textarea
            id="ps-bio"
            rows={3}
            className="w-full bg-input border border-border rounded-lg px-3.5 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all resize-none"
            {...register('bio')}
          />
        </div>
      </div>

      {/* Sticky save bar */}
      <div className="flex items-center justify-between bg-card border border-border rounded-xl px-5 py-3 shadow-card">
        {isDirty ? (
          <p className="text-sm text-warning font-medium flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-warning" />
            Unsaved changes
          </p>
        ) : saved ? (
          <p className="text-sm text-positive font-medium flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-positive" />
            All changes saved
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">No pending changes</p>
        )}
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm font-semibold rounded-lg hover:bg-primary/90 transition-colors btn-press disabled:opacity-70"
          >
            {isSaving ? (
              <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Save size={14} />
            )}
            {isSaving ? 'Saving...' : 'Save Profile'}
          </button>
        </div>
      </div>
    </form>
  );
}